import React from 'react';
import { Link } from 'react-router-dom';
import StudyStayLogo from '../components/StudyStayLogo';
import WaitlistForm from '../components/WaitlistForm';
import ScrollProgress from '../components/ScrollProgress';
import Reveal from '../components/Reveal';
import ParallaxBackground from '../components/ParallaxBackground';
import {
  Users,
  Building2,
  GraduationCap,
  Megaphone,
  BarChart3,
  Shield,
  Mail,
  CheckCircle,
  ArrowRight,
  Instagram,
} from 'lucide-react';

const partnerTypes = [
  {
    icon: GraduationCap,
    title: 'Universities & Schools',
    description: 'Offer students a safer, verified way to find housing without the noise.',
  },
  {
    icon: Building2,
    title: 'Housing Agencies',
    description: 'Streamline rental operations, reduce admin, and boost tenant satisfaction.',
  },
  {
    icon: Users,
    title: 'Student Organizations',
    description: 'Support your community with trusted listings and guidance.',
  },
];

const partnershipBenefits = [
  {
    icon: Megaphone,
    title: 'Co-marketing & visibility',
    description: 'We promote the partnership and create co-branded assets for your audience.',
  },
  {
    icon: BarChart3,
    title: 'Insights & reporting',
    description: 'Access anonymized insights on demand, interest, and conversion.',
  },
  {
    icon: Shield,
    title: 'Verified ecosystem',
    description: 'Build trust with verified landlords, secure contracts, and safe communication.',
  },
];

export default function ComingSoonPartnerships() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 relative overflow-hidden">
      <ScrollProgress />
      <ParallaxBackground />

      <header className="relative z-10 pt-8 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-center justify-between">
              <Link to="/coming-soon" className="flex items-center gap-3">
                <StudyStayLogo className="w-12 h-12" />
                <h1 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600">
                  StudyStay
                </h1>
              </Link>
              <div className="flex items-center gap-3">
                <Link
                  to="/coming-soon/landlord"
                  className="px-4 py-2 rounded-xl bg-white text-gray-700 font-semibold shadow-sm hover:shadow-md border border-gray-200 transition-all duration-200"
                >
                  Landlord
                </Link>
                <Link
                  to="/coming-soon/partnerships"
                  className="px-4 py-2 rounded-xl bg-primary-600 text-white font-semibold shadow-sm hover:shadow-md transition-all duration-200"
                >
                  Partnerships
                </Link>
                <a
                  href="https://www.instagram.com/studystay_app?igsh=eWZjcDYwbW9yZmYw&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="w-5 h-5 text-primary-600" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </header>

      <section className="relative z-10 pt-10 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
              Partnerships that improve student housing.
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-lg md:text-xl text-gray-700 mb-8">
              Work with StudyStay to deliver safer, faster, and more transparent housing for students.
            </p>
          </Reveal>
          <Reveal delay={250}>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <span className="px-4 py-2 rounded-full bg-white text-gray-700 font-semibold shadow-sm border border-gray-200">
                Verified listings
              </span>
              <span className="px-4 py-2 rounded-full bg-white text-gray-700 font-semibold shadow-sm border border-gray-200">
                Secure contracts
              </span>
              <span className="px-4 py-2 rounded-full bg-white text-gray-700 font-semibold shadow-sm border border-gray-200">
                Trusted communication
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 bg-white/60 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Who we partner with
            </h3>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {partnerTypes.map((partner, index) => {
              const Icon = partner.icon;
              return (
                <Reveal key={partner.title} delay={index * 120}>
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{partner.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{partner.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-10">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                What you get
              </h3>
              <p className="text-gray-600">
                A structured partnership that benefits your community and the student housing market.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {partnershipBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Reveal key={benefit.title} delay={index * 120}>
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                    <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 bg-white/60 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Start a partnership
            </h3>
            <p className="text-gray-600 mb-8">
              Tell us about your organization. We will follow up with a tailored collaboration plan.
            </p>
            <div className="max-w-md mx-auto">
              <WaitlistForm />
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-6">
              <Mail className="w-4 h-4" />
              <span>Prefer email? info@studystay.org</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-2">
              <ArrowRight className="w-4 h-4" />
              <span>We respond within 2 business days.</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <Reveal>
            <h4 className="text-lg font-bold text-gray-900 mb-3">Why now?</h4>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 text-primary-600 mr-2 mt-0.5" />
                Early partners get launch visibility across StudyStay channels.
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 text-primary-600 mr-2 mt-0.5" />
                Influence product direction with direct access to our team.
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-4 h-4 text-primary-600 mr-2 mt-0.5" />
                Provide students with safer housing options before peak season.
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      <footer className="relative z-10 border-t border-gray-200 py-8 px-4 sm:px-6 lg:px-8 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto text-center">
          <Reveal>
            <div className="flex items-center justify-center gap-2 mb-4">
              <StudyStayLogo className="w-6 h-6" />
              <span className="text-gray-700 font-semibold">StudyStay</span>
            </div>
            <p className="text-gray-600 font-medium mb-4">Coming Soon — Web & iOS</p>
            <div className="flex items-center justify-center gap-6 mb-4 flex-wrap">
              <a
                href="/privacy"
                className="text-sm text-gray-600 hover:text-primary-600 transition-colors duration-200"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                className="text-sm text-gray-600 hover:text-primary-600 transition-colors duration-200"
              >
                Terms of Service
              </a>
            </div>
            <p className="text-sm text-gray-500">© 2026 Nolf Technologies OÜ. All rights reserved.</p>
          </Reveal>
        </div>
      </footer>
    </div>
  );
}
